import { useEffect, useRef } from "react";

function useRevealOnScroll(ref) {
  useEffect(() => {
    const elements = ref.current?.querySelectorAll("[data-reveal]");
    if (!elements) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = el.dataset.delay || "0";
            setTimeout(() => {
              el.style.opacity = "1";
              el.style.transform = "translateY(0)";
            }, parseFloat(delay));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.15 },
    );

    elements.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(20px)";
      el.style.transition = "opacity 0.75s ease, transform 0.75s ease";
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
}

const pillars = [
  {
    num: "01",
    title: "Rooted in sound science",
    body: "Sound healing isn't mysticism — it's physics, neuroscience and embodied experience. I bring rigour to the work alongside presence.",
  },
  {
    num: "02",
    title: "Trained coach & counsellor",
    body: "Certified across relationships, trauma, grief and career. I hold whatever arrives — with skill, not just good intentions.",
  },
  {
    num: "03",
    title: "Parts work & IFS",
    body: "Trained in Internal Family Systems, I work with all the inner voices — especially the ones that hold the shadow. No bypassing.",
  },
  {
    num: "04",
    title: "Between worlds",
    body: "Indian and French roots, raised between Goa and Europe. Living between the spiritual and the pragmatic is my natural home.",
  },
];

export const AboutContent = () => {
  const sectionRef = useRef(null);
  useRevealOnScroll(sectionRef);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="bg-ink text-cream py-24 lg:py-32 px-8 lg:px-16"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        {/* ── Left: story ── */}
        <div>
          <p
            data-reveal
            data-delay="0"
            className="text-[0.65rem] tracking-[0.3em] uppercase text-earth-light opacity-60 mb-4 font-jost font-light"
          >
            Who I am
          </p>

          <h2
            data-reveal
            data-delay="80"
            className="font-cormorant font-light leading-[1.1] mb-8"
            style={{ fontSize: "clamp(2.4rem, 3.5vw, 3.8rem)" }}
          >
            Music found me
            <br />
            <em className="italic text-earth-light">before I found it.</em>
          </h2>

          <div
            data-reveal
            data-delay="160"
            className="w-10 h-0.5 bg-earth mb-8"
          />

          <div
            className="font-jost font-light text-cream/70 leading-[1.9] space-y-5 max-w-[500px]"
            style={{ fontSize: "clamp(0.9rem, 1.05vw, 1rem)" }}
          >
            <p data-reveal data-delay="200">
              I grew up in Goa with music always present — my father a musician,
              the ocean nearby, life lived close to the senses. Sound was never
              something I did. It was something I moved through.
            </p>
            <p data-reveal data-delay="260">
              I spent more than fifteen years as a senior front-end engineer —
              building interfaces that helped people navigate complex systems. I
              was good at it. But the deeper work was always about the human on
              the other side of the screen. Eventually I stopped pretending
              otherwise.
            </p>
            <p data-reveal data-delay="320">
              I trained as a coach and counsellor — across relationships,
              trauma, grief and career. I studied Internal Family Systems, the
              parts work that goes into the shadow without flinching. And I
              began integrating live music into the healing space, because I've
              always known: the body hears what the mind defends against.
            </p>
            <p data-reveal data-delay="380">
              Now I hold space at plant medicine ceremonies across the
              Netherlands, facilitate sound journeys, and work one-on-one with
              people in transition. The music and the inner work are not
              separate. They never were.
            </p>
          </div>
        </div>

        {/* ── Right: pillars ── */}
        <div data-reveal data-delay="200" className="flex flex-col">
          <p className="text-[0.65rem] tracking-[0.3em] uppercase text-earth-light opacity-60 mb-6 font-jost font-light">
            What I bring
          </p>

          <div className="flex flex-col divide-y divide-white/[0.07]">
            {pillars.map((p, i) => (
              <div
                key={p.num}
                data-reveal
                data-delay={`${200 + i * 80}`}
                className="grid grid-cols-[2rem_1fr] gap-5 py-6 group"
              >
                <span className="font-cormorant text-earth-light/50 text-sm pt-0.5 font-light">
                  {p.num}
                </span>
                <div>
                  <h3 className="font-cormorant font-light text-cream text-xl mb-2 group-hover:text-earth-light transition-colors duration-300">
                    {p.title}
                  </h3>
                  <p className="font-jost font-light text-cream/55 text-sm leading-[1.8]">
                    {p.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Subtle pull quote */}
          <div
            data-reveal
            data-delay="600"
            className="mt-10 border-l-2 border-earth pl-6"
          >
            <p
              className="font-cormorant italic font-light text-earth-light leading-[1.6]"
              style={{ fontSize: "clamp(1.1rem, 1.4vw, 1.35rem)" }}
            >
              "The body hears what the mind defends against. Music goes where
              words cannot."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
